import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Context Provider
import { AuthProvider } from './contexts/AuthContext';

// Custom Hooks
import useNavigationTracking from './hooks/useNavigationTracking';

// Layout Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import PermissionProtectedRoute from './components/layout/PermissionProtectedRoute';

// Page Components
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Rebalance from './pages/Rebalance';
import CoveredCallsAccountManagement from './pages/CoveredCallsAccountManagement';
import UserManagement from './pages/UserManagement';
import ClientHelper from './pages/ClientHelper';
import ClientAdmin from './pages/ClientAdmin';
import FileViewer from './components/FileViewer';
import ActivityDashboard from './components/ActivityDashboard';
import VectorInspector from './pages/VectorInspector';
import ChatPage from './pages/ChatPage';
import SharepointUpload from './pages/SharepointUpload';
import DocumentData from './pages/DocumentData';
import PromptManager from './pages/PromptManager';
import ReferenceValuesPage from './pages/ReferenceValuesPage';
import Resources from './pages/Resources';
import MassText from './pages/MassText';
import ClientVerification from './pages/ClientVerification';
import XSearchPage from './pages/XSearchPage';
import YouTubeManagementPage from './pages/YouTubeManagementPage';
import SECFilingPage from './pages/SECFilingPage';
import RebalancerDashboard from './pages/RebalancerDashboard';
import CashReservesDashboard from './pages/CashReservesDashboard';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import DirectedTrade from './pages/DirectedTrade';
import DocumentChatPage from './pages/DocumentChatPage';
import CompanyReviewsPage from './pages/CompanyReviewsPage';
import CashflowAdmin from './pages/CashflowAdmin';
import ClientForms from './pages/ClientForms';
import RedactorPage from './pages/RedactorPage';
import AutoTraderPage from './pages/AutoTraderPage';
import CompanyResearchPage from './pages/CompanyResearchPage';
import IpsScanner from './pages/IpsScanner';
import TradeTrackerPage from './pages/TradeTrackerPage';
import ExpenseReportsPage from './pages/ExpenseReportsPage';
import LstmTradingLab from './pages/LstmTradingLab';
import Help from './pages/Help';

// Navigation tracking component
const NavigationTracker = () => {
  useNavigationTracking();
  return null;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <NavigationTracker />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/help" element={
            <ProtectedRoute>
              <Layout>
                <Help />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/help/whats-new" element={
            <ProtectedRoute>
              <Layout>
                <Help view="whats-new" />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/help/:appId" element={
            <ProtectedRoute>
              <Layout>
                <Help view="app" />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Any signed-in employee; approving needs expense_reports_admin (checked in the page and the service). */}
          <Route path="/expense-reports" element={
            <ProtectedRoute>
              <Layout>
                <ExpenseReportsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/view-files" element={
            <PermissionProtectedRoute requiredPermission="rebalancer">
              <Layout>
                <FileViewer />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/activity" element={
            <PermissionProtectedRoute requiredPermission="activity_dashboard">
              <Layout>
                <ActivityDashboard />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/rebalance" element={
            <PermissionProtectedRoute requiredPermission="rebalancer">
              <Layout>
                <Rebalance />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/rebalancer-dashboard" element={
            <PermissionProtectedRoute requiredPermission="rebalancer">
              <Layout>
                <RebalancerDashboard />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/cash-reserves" element={
            <PermissionProtectedRoute requiredPermission="cash_reserves_monitor">
              <Layout>
                <CashReservesDashboard />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/transaction-history" element={
            <PermissionProtectedRoute requiredPermission="transaction_history">
              <Layout>
                <TransactionHistoryPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/directed-trade" element={
            <PermissionProtectedRoute requiredPermission="rebalancer">
              <Layout>
                <DirectedTrade />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/trade-tracker" element={
            <PermissionProtectedRoute requiredPermission="trade_tracker">
              <Layout>
                <TradeTrackerPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/lstm" element={
            <PermissionProtectedRoute requiredPermission="lstm">
              <Layout fullBleed>
                <LstmTradingLab />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/covered-calls" element={
            <PermissionProtectedRoute requiredPermission="cc_management">
              <Layout>
                <CoveredCallsAccountManagement />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/user-management" element={
            <ProtectedRoute>
              <Layout>
                <UserManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/client-helper" element={
            <PermissionProtectedRoute requiredPermission="client_helper">
              <Layout>
                <ClientHelper />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/vector-inspector" element={
            <PermissionProtectedRoute requiredPermission="vector_inspector">
              <Layout>
                <VectorInspector />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/chat" element={
            <PermissionProtectedRoute requiredPermission="chat">
              <Layout>
                <ChatPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/sharepoint-upload" element={
            <PermissionProtectedRoute requiredPermission="sharepoint_upload">
              <Layout>
                <SharepointUpload />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/ips-scanner" element={
            <PermissionProtectedRoute requiredPermission="ips_scanner">
              <Layout>
                <IpsScanner />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/document-data" element={
            <PermissionProtectedRoute requiredPermission="document_data">
              <Layout>
                <DocumentData />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/client-admin" element={
            <PermissionProtectedRoute requiredPermission="client_admin">
              <Layout>
                <ClientAdmin />
              </Layout>
            </PermissionProtectedRoute>
          } />
          {/* Reviewers of the annual IRS/SSA limits; the service checks the same permission. */}
          <Route path="/reference-values" element={
            <PermissionProtectedRoute requiredPermission="reference_values_admin">
              <Layout>
                <ReferenceValuesPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/prompt-manager" element={
            <PermissionProtectedRoute requiredPermission={["prompt_manager", "admin"]}>
              <Layout>
                <PromptManager />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/resources" element={
            <PermissionProtectedRoute requiredPermission="resources">
              <Layout>
                <Resources />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/mass-text" element={
            <PermissionProtectedRoute requiredPermission="mass_text">
              <Layout>
                <MassText />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/client-verification" element={
            <PermissionProtectedRoute requiredPermission="client_verification">
              <Layout>
                <ClientVerification />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/x-search" element={
            <PermissionProtectedRoute requiredPermission="x_admin">
              <Layout>
                <XSearchPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/youtube" element={
            <PermissionProtectedRoute requiredPermission="youtube_admin">
              <Layout>
                <YouTubeManagementPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/sec-filings" element={
            <PermissionProtectedRoute requiredPermission="sec_admin">
              <Layout>
                <SECFilingPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/doc-chat" element={
            <PermissionProtectedRoute requiredPermission="doc_chat">
              <Layout>
                <DocumentChatPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/company-reviews" element={
            <PermissionProtectedRoute requiredPermission="company_reviews">
              <Layout>
                <CompanyReviewsPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/cashflow-admin" element={
            <PermissionProtectedRoute requiredPermission="cashflow_admin">
              <Layout>
                <CashflowAdmin />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/client-forms" element={
            <PermissionProtectedRoute requiredPermission="cashflow_admin">
              <Layout>
                <ClientForms />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/redactor" element={
            <PermissionProtectedRoute requiredPermission="redactor">
              <Layout>
                <RedactorPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/auto-trader" element={
            <PermissionProtectedRoute requiredPermission="auto_trader">
              <Layout>
                <AutoTraderPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="/company-research" element={
            <PermissionProtectedRoute requiredPermission="company_research">
              <Layout>
                <CompanyResearchPage />
              </Layout>
            </PermissionProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
